import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { getSheetEvents } from '../utils/api';
import styles from './ActivitiesModal.module.css';

// Per-sheet activity log. Pulls /api/sheets/:id/events and renders the
// chronological feed: kind, actor, when, and a short summary of the
// payload (e.g. "Title changed from 'Old' to 'New'").

const KIND_COLORS = {
  uploaded:    'var(--green)',
  edited:      'var(--accent)',
  deleted:     'var(--red, #f87171)',
  restored:    'var(--blue)',
  published:   'var(--green)',
  unpublished: 'var(--yellow)',
};

const KIND_LABELS = {
  uploaded:    'Uploaded',
  edited:      'Edited',
  deleted:     'Deleted',
  restored:    'Restored',
  published:   'Published',
  unpublished: 'Unpublished',
};

export default function ActivitiesModal({ sheet, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sheet) return;
    let alive = true;
    getSheetEvents(sheet.id)
      .then((rows) => { if (alive) { setEvents(rows); setLoading(false); } })
      .catch((err) => { if (alive) { toast.error(err.message); setLoading(false); } });
    return () => { alive = false; };
  }, [sheet?.id]);

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <span className={styles.headerTitle}>Activity</span>
            <span className={styles.headerSub}>
              {sheet.sheet_number}{sheet.title ? ` · ${sheet.title}` : ''}
            </span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.body}>
          {loading && <div className={styles.empty}>Loading…</div>}

          {!loading && events.length === 0 && (
            <div className={styles.empty}>
              No activity logged yet. Events are recorded going forward —
              prior uploads / edits won't appear.
            </div>
          )}

          {events.map((e) => (
            <div key={e.id} className={styles.event}>
              <span
                className={styles.kindPill}
                style={{ background: KIND_COLORS[e.kind] || 'var(--text-dim)' }}
              >
                {KIND_LABELS[e.kind] || e.kind}
              </span>
              <div className={styles.eventBody}>
                <div className={styles.eventLine}>
                  <span className={styles.actor}>{e.actor_name || 'Unknown'}</span>
                  <span className={styles.summary}>{summarizePayload(e)}</span>
                </div>
                <div className={styles.eventTime}>
                  {e.created_at ? format(new Date(e.created_at), 'MMM d, yyyy h:mm a') : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Turn the event row into a short human sentence. For 'edited', show the
// changed field names + old → new for the simple ones (sheet_number,
// title, tags); skip the rest to keep the line short.
function summarizePayload(e) {
  const p = e.payload || {};
  switch (e.kind) {
    case 'uploaded':
      return p.filename
        ? `from ${p.filename}`
        : 'sheet uploaded';
    case 'edited': {
      const changed = p.changed || {};
      const prior   = p.prior   || {};
      const parts = [];
      for (const key of Object.keys(changed)) {
        const before = prior[key];
        const after  = changed[key];
        if (key === 'tags') {
          const b = Array.isArray(before) ? before.join(', ') : '';
          const a = Array.isArray(after)  ? after.join(', ')  : '';
          if (b !== a) parts.push(`tags: "${b || '—'}" → "${a || '—'}"`);
        } else if (typeof before === 'string' || before == null) {
          parts.push(`${pretty(key)}: "${before || '—'}" → "${after || '—'}"`);
        } else {
          parts.push(`${pretty(key)} changed`);
        }
      }
      return parts.length ? parts.join(' · ') : 'edited';
    }
    case 'deleted':  return 'moved to deleted';
    case 'restored': return 'restored from deleted';
    default:         return '';
  }
}

function pretty(key) {
  return key.replace(/_/g, ' ');
}
